import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { hasValidToken, validateTokenWithBackend, clearAllAppStorage } from '../utils/storageUtils';

interface AuthProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'manufacturer' | 'practitioner' | 'insurance';
  preventAdminAccess?: boolean;
}

export const AuthProtectedRoute: React.FC<AuthProtectedRouteProps> = ({
  children,
  requiredRole,
  preventAdminAccess = false
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const [isValidating, setIsValidating] = useState(true);

  // Validate JWT token on route access
  useEffect(() => {
    const validateAccess = async () => {
      // Check if we have a valid token format
      if (!hasValidToken()) {
        console.log('❌ No valid token found in ProtectedRoute, clearing storage and redirecting');
        clearAllAppStorage();
        setIsValidating(false);
        return;
      }

      // Validate token with backend
      const isValid = await validateTokenWithBackend();
      if (!isValid) {
        console.log('❌ Token validation failed in ProtectedRoute, clearing storage and redirecting');
        clearAllAppStorage();
      }

      setIsValidating(false);
    };

    validateAccess();
  }, []);

  // Show loading while validating
  if (isValidating) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check token validity first
  if (!hasValidToken()) {
    console.log('🚪 No valid token, redirecting to login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!user) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Prevent admin users from accessing certain routes (like dashboard)
  if (preventAdminAccess && user.role === 'admin') {
    return <Navigate to="/settings" replace />;
  }

  if (requiredRole && user.role !== requiredRole) {
    // Redirect to appropriate page based on user role
    if (user.role === 'admin') {
      return <Navigate to="/settings" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};
